import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/system';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import {
	Callout,
	Dialogue,
	FlexContainer,
	RuSecondaryButton,
	Spinner,
} from '@silverrailtech/srtlibrary';

import { gaEvent } from '../../../../utils/googleAnalytics';
import ActionButtons from './ActionButtons';
import messages from './messages';
import styles from './styles';
import { getReleaseStatus } from '../../utils';
import {
	SpinnerWrapper,
	DialogTitle,
	SuccessIcon,
	ErrorIcon,
	FailedTicketParagraph,
} from './ValueDocumentCouponsStyled';
import { MVD_TICKET_STATUS } from './constants';

const BUTTON_CONFIG = {
	remove: {
		color: 'error',
		idPrefix: 'srtBookingMTicketRemoveDeviceId',
		title: messages.btnRemoveDeviceId,
		description: messages.dlgRemoveDeviceIdDescription,
		primaryLabel: messages.btnRemoveDeviceId,
		buttonLabel: messages.btnRemoveDeviceId,
	},
};

const TitleContainerDiv = styled('div')({
	...styles.titleContainer,
});

const ButtonsGrid = styled(Grid)({
	marginLeft: 'unset !important',
	marginTop: 'unset !important',
});

const TitleSpan = styled('span')({
	...styles.title,
});

const ValueDocumentCoupons = ({
	coupons,
	orderId,
	returnCoupons,
	authorizeRefund,
	handleOpenReturnCouponsDialog,
	handleOpenAuthorizeRefundDialog,
	onMvdCouponSelect,
	mvdSelectedCoupons,
	mvdTicketStatus,
	handleMvdTicketAction,
	handleMvdTicketDialogClose,
	mvdTicketDeliveryData,
	mvdFailedTickets,
}) => {
	const [expanded, setExpanded] = useState(false);
	const [mvdTicketDialogConfig, setMvdTicketDialogConfig] = useState({
		open: false,
		title: {},
		description: {},
		primaryLabel: {},
		onConfirm: () => {},
		flowType: '',
	});

	const handleExpand = () => {
		gaEvent(
			expanded ? 'collapseValueDocumentCoupons' : 'expandValueDocumentCoupons',
		);
		setExpanded(!expanded);
	};

	const handleCouponSelect = (couponNumber, checked) => {
		onMvdCouponSelect(couponNumber, checked);
	};

	const closeDialog = () =>
		setMvdTicketDialogConfig({
			open: false,
			title: {},
			description: {},
			primaryLabel: {},
			onConfirm: () => {},
			flowType: '',
		});

	const handleLocalMvdTicketDialogClose = () => {
		if (
			mvdTicketStatus === MVD_TICKET_STATUS.SUCCESS ||
			mvdTicketStatus === MVD_TICKET_STATUS.PARTIAL
		) {
			mvdSelectedCoupons.forEach((couponNumber) => {
				onMvdCouponSelect(couponNumber, false);
			});
		}
		closeDialog();
		handleMvdTicketDialogClose();
	};

	const openMvdConfirmDialog = (flowType) => {
		const config = BUTTON_CONFIG[flowType];
		setMvdTicketDialogConfig({
			open: true,
			title: config.title,
			description: config.description,
			primaryLabel: config.primaryLabel,
			onConfirm: () => handleMvdTicketAction(),
			flowType,
		});
	};

	const cancelAction = {
		label: <FormattedMessage {...messages.btnCancel} />,
		onClick: () => {
			handleMvdTicketDialogClose();
			closeDialog();
		},
		color: 'neutral_1',
	};

	const closeAction = {
		...cancelAction,
		label: <FormattedMessage {...messages.btnClose} />,
		onClick: handleLocalMvdTicketDialogClose,
	};

	const confirmAction = {
		label: <FormattedMessage {...mvdTicketDialogConfig.primaryLabel} />,
		onClick: () => mvdTicketDialogConfig.onConfirm(),
		color: 'negative',
	};

	const getDialogPropsByStatus = (status) => {
		switch (status) {
			case MVD_TICKET_STATUS.LOADING:
				return {
					description: (
						<SpinnerWrapper padding="0">
							<Spinner width="64px" height="64px" className="spinner-padding" />
						</SpinnerWrapper>
					),
				};
			case MVD_TICKET_STATUS.SUCCESS:
				return {
					description: (
						<FlexContainer
							padding="0"
							direction="column"
							align="center"
							justify="center"
							gap="0"
						>
							<SuccessIcon />
							<FormattedMessage {...messages.lblSuccess} />
						</FlexContainer>
					),
					primaryAction: closeAction,
				};
			case MVD_TICKET_STATUS.PARTIAL:
				return {
					description: (
						<FlexContainer gap="8px" direction="column" padding="0">
							<FlexContainer
								padding="0"
								direction="column"
								align="center"
								justify="center"
								gap="0"
							>
								<ErrorIcon />
								<FormattedMessage {...messages.lblPartialFailure} />
							</FlexContainer>
							<FormattedMessage
								{...messages.dlgMTicketPartialFailureDescription}
							/>
							<Callout variant="important">
								{mvdFailedTickets.map((ticket) => (
									<FailedTicketParagraph key={ticket}>
										{ticket} failed.
									</FailedTicketParagraph>
								))}
							</Callout>
							<FormattedMessage {...messages.dlgMTicketReselectMessage} />
						</FlexContainer>
					),
					primaryAction: {
						...cancelAction,
						onClick: handleLocalMvdTicketDialogClose,
					},
				};
			case MVD_TICKET_STATUS.ERROR:
				return {
					description: (
						<>
							<Callout variant="important" style={{ marginBottom: '16px' }}>
								<FormattedMessage {...messages.dlgMTicketError} />
							</Callout>
							<FormattedMessage {...mvdTicketDialogConfig.description} />
						</>
					),
					primaryAction: cancelAction,
					secondaryAction: confirmAction,
				};
			default:
				return {
					description: (
						<FormattedMessage {...mvdTicketDialogConfig.description} />
					),
					primaryAction: cancelAction,
					secondaryAction: confirmAction,
				};
		}
	};

	return (
		<>
			{!coupons && (
				<TitleContainerDiv>
					<ButtonsGrid container spacing={8} alignItems="center">
						<TitleSpan>
							<FormattedMessage {...messages.lblTitle} />
						</TitleSpan>
						<ActionButtons
							orderId={orderId}
							returnCoupons={returnCoupons}
							authorizeRefund={authorizeRefund}
							handleOpenReturnCouponsDialog={handleOpenReturnCouponsDialog}
							handleOpenAuthorizeRefundDialog={handleOpenAuthorizeRefundDialog}
						/>
					</ButtonsGrid>
				</TitleContainerDiv>
			)}
			{!!coupons && !!coupons.length && (
				<>
					<TitleContainerDiv>
						<ButtonsGrid container spacing={8} alignItems="center">
							<TitleSpan>
								<FormattedMessage {...messages.lblTitle} />
							</TitleSpan>
							<IconButton onClick={handleExpand}>
								{expanded ? <ExpandLess /> : <ExpandMore />}
							</IconButton>
							<ActionButtons
								orderId={orderId}
								returnCoupons={returnCoupons}
								authorizeRefund={authorizeRefund}
								handleOpenReturnCouponsDialog={handleOpenReturnCouponsDialog}
								handleOpenAuthorizeRefundDialog={
									handleOpenAuthorizeRefundDialog
								}
							/>
						</ButtonsGrid>
					</TitleContainerDiv>
					<Collapse in={expanded}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>
										<FormattedMessage {...messages.lblNumber} />
									</TableCell>
									<TableCell>
										<FormattedMessage {...messages.lblType} />
									</TableCell>
									<TableCell>
										<FormattedMessage {...messages.lblReleaseStatus} />
									</TableCell>
									<TableCell>
										<FormattedMessage {...messages.lblReturnStartDateTime} />
									</TableCell>
									<TableCell>
										<FormattedMessage {...messages.lblUniqueTicketNumber} />
									</TableCell>
									<TableCell>
										<FormattedMessage {...messages.lblTravelSegments} />
									</TableCell>
									<TableCell>
										<FormattedMessage {...messages.lblOriginalCouponNumber} />
									</TableCell>
									{mvdTicketDeliveryData && (
										<TableCell>
											<FormattedMessage {...messages.lblSelect} />
										</TableCell>
									)}
								</TableRow>
							</TableHead>
							<TableBody>
								{coupons.map((coupon) => (
									<TableRow key={coupon.couponNumber}>
										<TableCell>{coupon.couponNumber}</TableCell>
										<TableCell>{coupon.couponType}</TableCell>
										<TableCell>
											{getReleaseStatus(
												coupon.isReleasable,
												coupon.releasedAt,
											) || ''}
										</TableCell>
										<TableCell>
											{coupon?.BookingCouponsReturnStartDateTime || '-'}
										</TableCell>
										<TableCell>
											{coupon.BookingCouponsUniqueTicketNumber}
										</TableCell>
										<TableCell>{coupon.description}</TableCell>
										<TableCell>{coupon.originalCouponNumber}</TableCell>
										{mvdTicketDeliveryData && (
											<TableCell>
												{mvdTicketDeliveryData.some(
													(mvd) =>
														mvd.BookingOrderTicketDeliveryMVDUniqueTicketNumber ===
															coupon.BookingCouponsUniqueTicketNumber?.trim() &&
														mvd.BookingOrderTicketDeliveryMVDCanResetDeviceId,
												) && (
													<Checkbox
														checked={mvdSelectedCoupons.includes(
															coupon.couponNumber,
														)}
														onChange={(e) =>
															handleCouponSelect(
																coupon.couponNumber,
																e.target.checked,
															)
														}
														disabled={
															mvdTicketStatus === MVD_TICKET_STATUS.LOADING
														}
													/>
												)}
											</TableCell>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
						{mvdTicketDeliveryData && mvdSelectedCoupons.length > 0 && (
							<FlexContainer
								direction="row"
								margin="16px 0 0 0"
								justify="flex-end"
							>
								<Button
									variant="contained"
									color={BUTTON_CONFIG.remove.color}
									id={`${BUTTON_CONFIG.remove.idPrefix}_${orderId}`}
									data-testid="mTicketAction-remove"
									style={{ width: '200px' }}
									disabled={mvdTicketStatus === MVD_TICKET_STATUS.LOADING}
									onClick={() => openMvdConfirmDialog('remove')}
								>
									<FormattedMessage {...BUTTON_CONFIG.remove.buttonLabel} />
								</Button>
							</FlexContainer>
						)}
					</Collapse>
				</>
			)}
			{mvdTicketDialogConfig.open && (
				<div data-testid={`mTicketDialog-${mvdTicketDialogConfig.flowType}`}>
					<Dialogue
						open={mvdTicketDialogConfig.open}
						TitleComponent={DialogTitle}
						ButtonComponent={RuSecondaryButton}
						title={<FormattedMessage {...mvdTicketDialogConfig.title} />}
						{...getDialogPropsByStatus(mvdTicketStatus)}
					/>
				</div>
			)}
		</>
	);
};

ValueDocumentCoupons.propTypes = {
	coupons: PropTypes.arrayOf(
		PropTypes.shape({
			couponNumber: PropTypes.string,
			couponType: PropTypes.string,
			isReleasable: PropTypes.any,
			couponReceivedDate: PropTypes.string,
			origCode: PropTypes.any,
			origStationName: PropTypes.any,
			destCode: PropTypes.any,
			destStationName: PropTypes.any,
			description: PropTypes.string,
			numPax: PropTypes.number,
			numPaxCouponsIssued: PropTypes.number,
			tsDepartTime: PropTypes.number,
			releasedAt: PropTypes.any,
			originalCouponNumber: PropTypes.any,
			BookingCouponsUniqueTicketNumber: PropTypes.any,
			BookingCouponsReturnStartDateTime: PropTypes.string,
		}),
	),
	orderId: PropTypes.string.isRequired,
	returnCoupons: PropTypes.bool.isRequired,
	authorizeRefund: PropTypes.bool.isRequired,
	handleOpenReturnCouponsDialog: PropTypes.func.isRequired,
	handleOpenAuthorizeRefundDialog: PropTypes.func.isRequired,
	onMvdCouponSelect: PropTypes.func.isRequired,
	mvdSelectedCoupons: PropTypes.array.isRequired,
	mvdTicketStatus: PropTypes.string.isRequired,
	handleMvdTicketAction: PropTypes.func.isRequired,
	handleMvdTicketDialogClose: PropTypes.func.isRequired,
	mvdTicketDeliveryData: PropTypes.array,
	mvdFailedTickets: PropTypes.array,
};

export { ValueDocumentCoupons as ValueDocumentCouponsAlias };

export default ValueDocumentCoupons;
